/**
 * AutoDelegarAfipDialog — dispara el bot de auto-delegación AFIP (ORR-111).
 *
 * Flujo:
 *   1. Lista los servicios ya delegados (`useServiciosDelegadosCliente`) y los
 *      pre-deselecciona (fix R2-H5) para no re-disparar logins innecesarios.
 *      Cuenta como delegado solo lo delegado al representante actual
 *      (`useRepresentanteAfip`). Un servicio delegado solo a otro representante
 *      queda habilitado. Sin el representante, cuenta cualquier delegación activa.
 *   2. Pide PIN (4 dígitos) y selección de servicios. El bot entra con la clave
 *      de ARCA guardada en Claves (ORR-637): sin ella, avisa y no deja enviar.
 *   3. Opcional: registrar el consentimiento firmado del cliente (Letrita A).
 *   4. Dispara `useIniciarAutoDelegacion` → POST Background Function con un
 *      `jobId` generado en el front.
 *   5. Pantalla de progreso en vivo: poll de `afip_delegacion_progress` del
 *      job cada 3s.
 *   6. Cuando la fila `job` llega a `completado` o `fallido`, muestra el
 *      resultado y cierra. Sin esa fila a los 2 minutos, avisa y habilita el
 *      reintento (ORR-579).
 *
 * Validación: TanStack Form + Zod (`autoDelegarAfipSchema`).
 */
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react'
import {
  Loader2,
  Sparkles,
  CheckCircle2,
  XCircle,
  Clock,
} from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'

import { getFieldError } from '@/components/clientes/wizard/useClienteForm'
import { useClientesConClaveArca } from '@/components/clientes/useClientesConClaveArca'
import {
  useIniciarAutoDelegacion,
  useRepresentanteAfip,
  useServiciosDelegadosCliente,
  useProgressAutoDelegacion,
  useRefrescarTrasAutoDelegacion,
} from '@/hooks/useAfipDelegacion'
import {
  SERVICIOS_AFIP_UI,
  type ServicioCodigo,
} from '@/services/afipDelegacionService'
import { useAutoDelegarAfipForm } from './useAutoDelegarAfipForm'
import {
  PLAZO_ARRANQUE_MS,
  WEBSERVICES,
  estadoDelJob,
  resumenDelJob,
} from './autoDelegarAfipCierre'

interface AutoDelegarAfipDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  clienteId: string
  organizacionId: string
  cuitCliente: string
  nombreCliente: string
  /** Estado del casillero (delegacion_eventanilla). Si != 'activa', los
   * webservices ya delegados pueden necesitar el Paso B (Computador Fiscal) y
   * se permiten re-delegar para completarlo. */
  delegacionEventanilla?: string
}

const ORDEN_SERVICIOS: ServicioCodigo[] = [
  'wsccomu',
  'wsmtxca',
  'sistema_registral',
  'monotributo',
]

export function AutoDelegarAfipDialog({
  open,
  onOpenChange,
  clienteId,
  organizacionId,
  cuitCliente,
  nombreCliente,
  delegacionEventanilla,
}: AutoDelegarAfipDialogProps) {
  const iniciar = useIniciarAutoDelegacion()

  // Si el casillero no está activo, los webservices ya delegados pueden tener
  // el Paso B (Computador Fiscal) faltante → se permiten re-delegar para
  // completarlo (el backend es idempotente: paso A da SERVICIO_YA_DELEGADO y
  // corre la Fase B igual).
  const pasoBPendiente =
    delegacionEventanilla != null && delegacionEventanilla !== 'activa'
  const esRemediable = useCallback(
    (codigo: ServicioCodigo) => pasoBPendiente && WEBSERVICES.includes(codigo),
    [pasoBPendiente],
  )
  // El casillero ya delegado se puede volver a tildar aunque esté activo, para
  // completar un computador fiscal que le falte (ORR-654). No viene tildado.
  const esCompletable = (codigo: ServicioCodigo) => codigo === 'wsccomu'
  const { data: serviciosDelegados } = useServiciosDelegadosCliente(open ? clienteId : null)
  const representanteQuery = useRepresentanteAfip(open)
  const representante = representanteQuery.data?.replace(/\D/g, '') || null
  // The representative answered, failed or is not going to be asked. Until then the pre-selection waits.
  const representanteResuelto = !representanteQuery.isPending || representanteQuery.fetchStatus === 'idle'
  // The bot logs in with the stored ARCA key. Only a confirmed absence blocks: if the lookup fails, the server decides.
  // A failed refetch keeps the previous Set, which may predate a key saved since then.
  const { data: clientesConClave, isError: errorClaves } = useClientesConClaveArca(open)
  const sinClave = !!clientesConClave && !errorClaves && !clientesConClave.has(clienteId)

  // Run in progress: its jobId, the requested services, when it was
  // dispatched, and the clock the start deadline is measured with. `ahora`
  // only moves when that deadline passes, so the render stays pure.
  const [ejecucion, setEjecucion] = useState<{
    jobId: string
    servicios: ServicioCodigo[]
    desde: number
    ahora: number
  } | null>(null)
  const isProcessing = !!ejecucion
  const refrescar = useRefrescarTrasAutoDelegacion()

  const { data: progressRows } = useProgressAutoDelegacion(ejecucion?.jobId, {
    polling: isProcessing,
  })

  const estadoJob = useMemo(
    () =>
      ejecucion
        ? estadoDelJob(progressRows ?? [], ejecucion.jobId, ejecucion.desde, ejecucion.ahora)
        : null,
    [progressRows, ejecucion],
  )
  const tipoJob = estadoJob?.tipo ?? null
  const terminado = tipoJob === 'completado' || tipoJob === 'fallido'

  // Without the `job` row, move the clock once the start deadline passes.
  useEffect(() => {
    if (tipoJob !== 'esperando' || !ejecucion) return
    const t = setTimeout(
      () => setEjecucion((e) => (e ? { ...e, ahora: Date.now() } : e)),
      ejecucion.desde + PLAZO_ARRANQUE_MS - Date.now(),
    )
    return () => clearTimeout(t)
  }, [tipoJob, ejecucion])

  // Effect 1: on the terminal state, toast once and refresh the client.
  const notificadoRef = useRef<string | null>(null)
  useEffect(() => {
    if (!terminado || !ejecucion || notificadoRef.current === ejecucion.jobId) return
    notificadoRef.current = ejecucion.jobId

    const { listos, total, mensaje } = resumenDelJob(
      progressRows ?? [],
      ejecucion.jobId,
      ejecucion.servicios,
    )
    if (tipoJob === 'completado') {
      toast.success(`AFIP delegada: ${listos}/${total} servicios OK`)
    } else if (listos > 0) {
      toast.warning(`AFIP parcial: ${listos}/${total} OK${mensaje ? ` — ${mensaje.slice(0, 100)}` : ''}`)
    } else {
      toast.error(mensaje ? `AFIP falló: ${mensaje.slice(0, 100)}` : 'AFIP falló — revisá el historial')
    }
    refrescar(clienteId, organizacionId)
  }, [terminado, tipoJob, ejecucion, progressRows, refrescar, clienteId, organizacionId])

  // Effect 2: close 1500 ms after the terminal state. It depends only on that
  // state, so progress arriving in the meantime does not restart the timer.
  const cerrar = useEffectEvent(() => {
    setEjecucion(null)
    onOpenChange(false)
  })
  useEffect(() => {
    if (!terminado) return
    const t = setTimeout(cerrar, 1500)
    return () => clearTimeout(t)
  }, [terminado])

  // `yaActivos`: servicio_codigo → active row of the current representative, which disables the checkbox. Without
  // the representative, any active row counts. `delAnterior`: services whose active rows are all of another
  // representative.
  const { yaActivos, delAnterior } = useMemo(() => {
    const activos = new Map<string, { fecha: string | null }>()
    const anteriores = new Set<string>()
    for (const row of serviciosDelegados ?? []) {
      if (row.estado !== 'activa') continue
      if (!representante || row.cuit_representante.replace(/\D/g, '') === representante) {
        activos.set(row.servicio_codigo, { fecha: row.fecha_delegacion })
      } else {
        anteriores.add(row.servicio_codigo)
      }
    }
    for (const codigo of activos.keys()) anteriores.delete(codigo)
    return { yaActivos: activos, delAnterior: anteriores }
  }, [serviciosDelegados, representante])

  // Estado por servicio del job actual (en_curso / completado / fallido).
  // Trackea las dos fases: Paso A (`delegando_*`) y, para webservices, Paso B
  // (`computador_fiscal_*`). El Paso B pisa al A porque es la fase posterior:
  // un webservice no está "Listo" hasta que se le asigna el Computador Fiscal.
  const estadoServicios = useMemo(() => {
    const map = new Map<
      ServicioCodigo,
      {
        status: 'en_curso' | 'completado' | 'fallido'
        message: string | null
        fase: 'delegacion' | 'computador_fiscal'
      }
    >()
    for (const row of progressRows ?? []) {
      const m = /^delegando_(.+)$/.exec(row.step)
      if (!m) continue
      map.set(m[1] as ServicioCodigo, {
        status: row.status as 'en_curso' | 'completado' | 'fallido',
        message: row.message,
        fase: 'delegacion',
      })
    }
    for (const row of progressRows ?? []) {
      const m = /^computador_fiscal_(.+)$/.exec(row.step)
      if (!m) continue
      map.set(m[1] as ServicioCodigo, {
        status: row.status as 'en_curso' | 'completado' | 'fallido',
        message: row.message,
        fase: 'computador_fiscal',
      })
    }
    return map
  }, [progressRows])

  const { form } = useAutoDelegarAfipForm({
    onSubmit: async (values) => {
      const { jobId } = await iniciar.mutateAsync({
        pin: values.pin,
        input: {
          clienteId,
          organizacionId,
          servicios: values.servicios,
          guardarClave: values.guardarClave,
          consentimientoUrl: values.consentimientoUrl ?? undefined,
        },
        clienteNombre: nombreCliente,
      })
      const desde = Date.now()
      // Activar polling. El dialog queda abierto mostrando progreso en vivo.
      setEjecucion({ jobId, servicios: values.servicios, desde, ahora: desde })
    },
  })

  // Pre-deselect servicios ya activos al abrir / cuando llega la query.
  // Usamos ref (no state) para evitar la regla de React 19 que prohíbe
  // llamar setState() directamente dentro de un useEffect.
  const bootstrappedRef = useRef(false)
  useEffect(() => {
    if (!open || bootstrappedRef.current || !serviciosDelegados || !representanteResuelto) return
    // Pendientes = no delegados aún + webservices delegados pero con Paso B
    // faltante (casillero != activa), que vienen pre-tildados para completar.
    const pendientes = ORDEN_SERVICIOS.filter(
      (codigo) => !yaActivos.has(codigo) || esRemediable(codigo),
    )
    form.setFieldValue('servicios', pendientes)
    bootstrappedRef.current = true
  }, [open, serviciosDelegados, representanteResuelto, yaActivos, form, esRemediable])

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      bootstrappedRef.current = false
      // Si está procesando, no resetear el state — el flow sigue en backend.
      // Si reabre el dialog, el polling del job retoma donde estaba.
    }
    onOpenChange(next)
  }

  const isSubmitting = iniciar.isPending || isProcessing

  // Vista de progreso en vivo cuando el flow está corriendo en background
  const serviciosEnProceso = ejecucion?.servicios ?? []

  if (isProcessing) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 text-primary animate-spin" />
              Auto-delegando AFIP — {nombreCliente}
            </DialogTitle>
            <DialogDescription>
              El bot está corriendo en background y tarda de 1 a 3 minutos.
              Podés cerrar este diálogo, el proceso continúa.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {serviciosEnProceso.map((codigo) => {
              const def = SERVICIOS_AFIP_UI[codigo]
              const estado = estadoServicios.get(codigo)
              // Un webservice solo está "Listo" cuando completó el Paso B
              // (Computador Fiscal); los interactivos, con el Paso A.
              const esWebservice = WEBSERVICES.includes(codigo)
              const done =
                estado?.status === 'completado' &&
                (!esWebservice || estado.fase === 'computador_fiscal')
              const failed = estado?.status === 'fallido'
              const activo = !done && !failed && estado != null
              const Icon = done
                ? CheckCircle2
                : failed
                  ? XCircle
                  : activo
                    ? Loader2
                    : Clock
              const iconClass = done
                ? 'text-emerald-600 dark:text-emerald-400'
                : failed
                  ? 'text-destructive'
                  : activo
                    ? 'text-primary animate-spin'
                    : 'text-muted-foreground'
              const label = failed
                ? estado?.message?.slice(0, 60) || 'Falló'
                : done
                  ? 'Listo'
                  : estado?.fase === 'computador_fiscal'
                    ? 'Asignando Computador Fiscal…'
                    : estado?.status === 'completado'
                      ? 'Delegado, falta Computador Fiscal…'
                      : estado?.status === 'en_curso'
                        ? 'Delegando…'
                        : 'En cola'
              return (
                <div
                  key={codigo}
                  className="flex items-start gap-3 rounded-md border p-3"
                >
                  <Icon className={`h-5 w-5 mt-0.5 ${iconClass}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{def.nombre}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {label}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>

          {tipoJob === 'sin_arranque' && (
            <Alert variant="destructive">
              <AlertDescription>
                No recibimos confirmación de que el proceso arrancó. Revisá el historial antes de volver a intentar.
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            {tipoJob === 'sin_arranque' && (
              <Button type="button" variant="outline" onClick={() => setEjecucion(null)}>
                Volver a intentar
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Cerrar (el proceso continúa)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Auto-delegar AFIP — {nombreCliente}
          </DialogTitle>
          <DialogDescription>
            Menta va a entrar a AFIP con la clave fiscal del cliente (CUIT {cuitCliente}) guardada
            en Claves para delegar al estudio los servicios seleccionados. Tarda 1–3 minutos.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            // Until the representative is resolved, `servicios` still holds the defaults, not the pre-selection.
            if (!representanteResuelto || sinClave) return
            void form.handleSubmit()
          }}
          className="space-y-5"
        >
          {/* Servicios */}
          <form.Field name="servicios">
            {(field) => {
              const seleccionados = new Set(field.state.value)
              const toggle = (codigo: ServicioCodigo, checked: boolean) => {
                const next = new Set(seleccionados)
                if (checked) next.add(codigo)
                else next.delete(codigo)
                field.handleChange(Array.from(next) as ServicioCodigo[])
              }
              return (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Servicios a delegar</Label>
                  <div className="space-y-2 rounded-md border p-3">
                    {ORDEN_SERVICIOS.map((codigo) => {
                      const def = SERVICIOS_AFIP_UI[codigo]
                      const activo = yaActivos.get(codigo)
                      // Un servicio activo es re-seleccionable solo si es un
                      // webservice con Paso B faltante (casillero != activa).
                      const remediable = !!activo && esRemediable(codigo)
                      const completable = !!activo && !remediable && esCompletable(codigo)
                      const disabled =
                        (!!activo && !remediable && !completable) || isSubmitting || !representanteResuelto
                      const checked =
                        remediable || completable
                          ? seleccionados.has(codigo)
                          : seleccionados.has(codigo) && !activo
                      return (
                        <div key={codigo} className="flex items-start gap-3">
                          <Checkbox
                            id={`servicio-${codigo}`}
                            checked={checked}
                            disabled={disabled}
                            onCheckedChange={(v) => toggle(codigo, v === true)}
                            className="mt-0.5"
                          />
                          <div className="flex-1 min-w-0">
                            <Label
                              htmlFor={`servicio-${codigo}`}
                              className={`text-sm ${disabled ? 'text-muted-foreground' : ''}`}
                            >
                              {def.nombre}
                            </Label>
                            <p className="text-xs text-muted-foreground">{def.descripcion}</p>
                            {activo && remediable ? (
                              <p className="text-xs text-amber-600 dark:text-amber-400 inline-flex items-center gap-1 mt-1">
                                <Clock className="h-3 w-3" />
                                Paso B pendiente — re-delegar para completar el casillero
                              </p>
                            ) : activo ? (
                              <p className="text-xs text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1 mt-1">
                                <CheckCircle2 className="h-3 w-3" />
                                Ya delegado
                                {activo.fecha
                                  ? ` el ${new Date(activo.fecha).toLocaleDateString('es-AR')}`
                                  : ''}
                                {completable ? '. Tildalo para volver a asignar los computadores fiscales' : ''}
                              </p>
                            ) : delAnterior.has(codigo) ? (
                              <p className="text-xs text-amber-600 dark:text-amber-400 inline-flex items-center gap-1 mt-1">
                                <Clock className="h-3 w-3" />
                                Delegado al representante anterior: volvé a delegarlo
                              </p>
                            ) : null}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-destructive text-sm">
                      {getFieldError(field.state.meta.errors)}
                    </p>
                  )}
                </div>
              )
            }}
          </form.Field>

          {sinClave && (
            <Alert variant="destructive">
              <AlertDescription>
                Este cliente no tiene su clave fiscal de ARCA guardada. Cargala en la solapa
                Claves de la ficha y volvé a abrir la auto-delegación.
              </AlertDescription>
            </Alert>
          )}

          {/* PIN */}
          <form.Field name="pin">
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="pin">PIN de Vault (4 dígitos)</Label>
                <Input
                  id="pin"
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  autoComplete="off"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value.replace(/\D/g, ''))}
                  onBlur={field.handleBlur}
                  disabled={isSubmitting}
                  placeholder="••••"
                  className="font-mono text-center tracking-[0.5em] max-w-[140px]"
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-destructive text-sm">
                    {getFieldError(field.state.meta.errors)}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          {/* Opt-in guardar clave */}
          <form.Field name="guardarClave">
            {(field) => (
              <div className="rounded-md border border-amber-300/60 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-950/20 p-3 space-y-2">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="guardarClave"
                    checked={field.state.value}
                    onCheckedChange={(v) => field.handleChange(v === true)}
                    disabled={isSubmitting}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <Label htmlFor="guardarClave" className="text-sm font-medium">
                      Registrar el consentimiento firmado del cliente
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Opcional. Guarda, como constancia, el link al documento en el que{' '}
                      {nombreCliente} autoriza al estudio a usar su clave fiscal. No cambia qué
                      servicios se delegan.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </form.Field>

          {/* Consentimiento URL (sólo si guardarClave) */}
          <form.Subscribe selector={(state) => state.values.guardarClave}>
            {(guardar) =>
              guardar ? (
                <form.Field name="consentimientoUrl">
                  {(field) => (
                    <div className="space-y-1.5">
                      <Label htmlFor="consentimientoUrl">
                        URL del consentimiento firmado (PDF)
                      </Label>
                      <Input
                        id="consentimientoUrl"
                        type="url"
                        value={field.state.value ?? ''}
                        onChange={(e) => field.handleChange(e.target.value || null)}
                        onBlur={field.handleBlur}
                        disabled={isSubmitting}
                        placeholder="https://..."
                      />
                      <p className="text-xs text-muted-foreground">
                        Subí el consentimiento firmado a Storage primero y pegá la URL acá.
                        Plantilla: <code>docs/templates/consentimiento-afip-clave-fiscal.md</code>
                      </p>
                      {field.state.meta.errors.length > 0 && (
                        <p className="text-destructive text-sm">
                          {getFieldError(field.state.meta.errors)}
                        </p>
                      )}
                    </div>
                  )}
                </form.Field>
              ) : null
            }
          </form.Subscribe>

          {iniciar.isError && (
            <Alert variant="destructive">
              <AlertDescription>
                {iniciar.error instanceof Error
                  ? iniciar.error.message
                  : 'Error al iniciar la auto-delegación'}
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting || !representanteResuelto || sinClave}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Auto-delegando...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Auto-delegar ahora
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
